// Everything the admin can add lives here. To add a new section, add an entry:
// the list, the form and the API calls are generated from it.
//
// Field types: text, number, textarea, select, checkbox,
//   image  -> ONE file, sent as the file field "image", stored as image_url
//   images -> SEVERAL files, sent as repeated file field "images" (max = most allowed)
//   tags   -> a numbered list the admin builds one by one; saved as comma separated text.
//             `suggestionsFrom: 'brand'` shows tap-to-add chips for the models of the chosen brand.
//   select -> options come from `options` (a list) or, with optionsFrom: 'categories' or 'brands', from that admin section.
//             `clears: ['models']` empties those fields when this one changes.
// Optional field props: help (line under the input), hint(values) (live line that replaces help).
// Optional resource props: sub(item), badge(item), flag(item) (status tag), quick (fields editable from the list; give `options` for a dropdown),
//   validate(values) -> error text or ''.
const on = { key: 'is_active', label: 'Show on the website', type: 'checkbox', def: true }
const order = {
  key: 'sort_order', label: 'Display order', type: 'number', def: 0, min: 0,
  help: 'Lower numbers appear first. Use 1, 2, 3… to arrange them.',
}

const STATUS = ['In stock', 'Out of stock', 'Coming soon']

// percentage off, or 0 when there is no valid sale.
// No discount is shown unless the product is in stock (Out of stock / Coming soon hide it).
const pctOff = (p) => {
  if (p.status && p.status !== 'In stock') return 0
  const sale = Number(p.sale_price)
  const price = Number(p.price)
  return sale > 0 && price > 0 && sale < price ? Math.round((1 - sale / price) * 100) : 0
}

export const RESOURCES = {
  categories: {
    label: 'Categories', singular: 'category', title: 'name',
    sub: (c) => `Display order: ${c.sort_order ?? 0}`,
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, max: 100, placeholder: 'e.g. Premium Case Covers' },
      { key: 'image', label: 'Image', type: 'image', required: true },
      order, on,
    ],
  },
  brands: {
    label: 'Phone brands', singular: 'brand', title: 'name',
    sub: (b) => `${String(b.models || '').split(',').filter((m) => m.trim()).length} models · Display order: ${b.sort_order ?? 0}`,
    fields: [
      { key: 'name', label: 'Brand name', type: 'text', required: true, max: 60, placeholder: 'e.g. Nothing' },
      {
        key: 'models', label: 'Phone models', type: 'tags', placeholder: 'Type a model and press Enter',
        help: 'Add each model this brand makes, for example Phone (2). You can pick them when adding products.',
        empty: 'No models added yet.',
      },
      order, on,
    ],
  },
  products: {
    label: 'Products', singular: 'product', title: 'name',
    quickLabel: 'Price & stock',
    quick: [
      { key: 'price', label: 'Price (₹)' },
      { key: 'sale_price', label: 'Sale price (₹)', placeholder: 'No sale' },
      { key: 'status', label: 'Stock status', options: STATUS, def: 'In stock' },
    ],
    badge: (p) => (pctOff(p) ? `${pctOff(p)}% off` : ''),
    flag: (p) => (p.status && p.status !== 'In stock' ? p.status : ''),
    sub: (p) => [
      p.category,
      p.brand,
      pctOff(p) ? `₹${p.sale_price} (was ₹${p.price})` : p.price ? `₹${p.price}` : '',
    ].filter(Boolean).join(' · '),
    validate: (v) => {
      const sale = v.sale_price
      if (sale === '' || sale == null) return ''
      return Number(sale) >= Number(v.price) ? 'Sale price must be lower than the price.' : ''
    },
    fields: [
      { key: 'name', label: 'Product name', type: 'text', required: true, max: 150 },
      { key: 'category', label: 'Category', type: 'select', optionsFrom: 'categories', required: true },
      {
        key: 'brand', label: 'Brand', type: 'select', optionsFrom: 'brands', clears: ['models'],
        emptyLabel: 'All brands (fits any phone)',
        help: 'The phone brand this product is for. Leave empty for products that fit any phone. Add new brands under Phone brands.',
      },
      { key: 'price', label: 'Price (₹)', type: 'number', required: true, min: 0 },
      {
        key: 'sale_price', label: 'Sale price (₹)', type: 'number', min: 0,
        help: 'Optional. Leave empty when there is no sale. Must be lower than the price.',
        hint: (v) => (pctOff(v) ? `Customers will see ${pctOff(v)}% off` : ''),
      },
      {
        key: 'status', label: 'Stock status', type: 'select', options: STATUS, def: 'In stock', noEmpty: true,
        help: 'Out of stock and Coming soon products stay visible but cannot be bought.',
      },
      { key: 'images', label: 'Images', type: 'images', required: true, max: 6 },
      { key: 'video', label: 'Product video', type: 'video', help: 'Optional. Shown as the last slide next to the product photos. Use MP4 (H.264) for best results; iPhone .mov files may not play in every browser.' },
      { key: 'desc', label: 'Description', type: 'textarea' },
      {
        key: 'models', label: 'Compatible phones', type: 'tags', placeholder: 'Or type a model and press Enter',
        suggestionsFrom: 'brand',
        help: 'Tap the models it fits. Leave the list empty to fit every model of the brand.',
      },
      { key: 'is_new', label: 'Mark as New', type: 'checkbox', def: false },
      { key: 'is_best', label: 'Mark as Best seller', type: 'checkbox', def: false },
      on,
    ],
  },
  offers: {
    label: 'Top bar offers', singular: 'offer', title: 'text',
    fields: [
      { key: 'text', label: 'Message', type: 'text', required: true, max: 160 },
      order, on,
    ],
  },
  reviews: {
    label: 'Reviews', singular: 'review', title: 'name',
    sub: (r) => `${'★'.repeat(Number(r.stars) || 0)}  ${r.text || ''}`,
    fields: [
      { key: 'name', label: 'Customer name', type: 'text', required: true, max: 80 },
      { key: 'text', label: 'Review', type: 'textarea', required: true },
      { key: 'stars', label: 'Stars (1 to 5)', type: 'number', def: 5, min: 1, max: 5 },
      on,
    ],
  },
}